import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { store } from "../../../../redux/store";
import { SMS_CONTACTS_QUERY_KEY } from "../../sms/contacts/hooks/useSmsContactsQuery";
import { ContactType, IContact, ICreateContact, IUpdateContact } from "../../types";
import { createContact, deleteContact, getContacts, ILinkWorkforcePhone, linkWorkforce, updateContact } from "../actions/contacts.actions";
import { VR_BULK_RESOLVE_QUERY_KEY } from "./useBulkContactResolver";
import { BULK_RESOLVE_QUERY_KEY } from "./useBulkResolveContacts";

/** Keep SMS/call UIs in sync after contact create/update/delete/link. */
export const invalidateContactResolutionQueries = async (queryClient: QueryClient, companyId?: number) => {
  // Drop cached name lookups immediately (SMS list/chat + call widgets/notifications).
  queryClient.removeQueries({ queryKey: [...BULK_RESOLVE_QUERY_KEY] });
  queryClient.removeQueries({ queryKey: [...VR_BULK_RESOLVE_QUERY_KEY] });
  queryClient.removeQueries({ queryKey: ["caller-contacts-self"] });

  const invalidations: Promise<unknown>[] = [
    queryClient.invalidateQueries({
      queryKey: [...SMS_CONTACTS_QUERY_KEY],
      refetchType: "all",
    }),
    queryClient.invalidateQueries({
      queryKey: ["call-history"],
      refetchType: "all",
    }),
    // Covers list queries and any resolve-bulk/contacts-* keys under "contacts"
    queryClient.invalidateQueries({
      queryKey: ["contacts"],
      refetchType: "all",
    }),
  ];

  if (companyId != null) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: ["contacts", companyId],
        refetchType: "all",
      }),
    );
  }

  await Promise.all(invalidations);
};

export const useGetContacts = (
  companyId: number,
  page?: number,
  limit?: number,
  search?: string,
  options?: { enabled?: boolean; type?: ContactType | string | null },
) => {
  return useQuery({
    queryKey: ["contacts", companyId, page, limit, search, options?.type ?? null],
    queryFn: () => getContacts(companyId, page, limit, search, options?.type),
    enabled: !!companyId && (options?.enabled ?? true),
  });
};

export const useCreateContact = (companyId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ICreateContact) => createContact(payload),
    onSuccess: async () => {
      await invalidateContactResolutionQueries(queryClient, companyId);
    },
  });
};

export const useUpdateContact = (companyId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, payload }: { uid: string; payload: IUpdateContact }) => updateContact(uid, payload),
    onSuccess: async () => {
      await invalidateContactResolutionQueries(queryClient, companyId);
    },
  });
};

export const useDeleteContact = (companyId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uid: string) => deleteContact(uid),
    onSuccess: async () => {
      await invalidateContactResolutionQueries(queryClient, companyId);
    },
  });
};

export const useLinkWorkforce = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workforceUid, phoneNumbers }: { workforceUid: string; phoneNumbers: ILinkWorkforcePhone[] }) =>
      linkWorkforce(workforceUid, phoneNumbers),
    onSuccess: async () => {
      await invalidateContactResolutionQueries(queryClient);
    },
  });
};

export const performSmartContactCleanup = async (
  phoneNumberOrNumbers: string | string[],
  companyId?: number,
  queryClient?: QueryClient,
  targetContactUid?: string,
) => {
  const effectiveCompanyId = companyId || (store.getState() as any)?.userState?.company?.id;

  const phoneList: string[] = Array.isArray(phoneNumberOrNumbers)
    ? phoneNumberOrNumbers.filter(Boolean)
    : phoneNumberOrNumbers
      ? [phoneNumberOrNumbers]
      : [];

  console.log("[performSmartContactCleanup] Starting cleanup:", {
    phoneList,
    effectiveCompanyId,
    targetContactUid,
  });

  if (phoneList.length === 0 && !targetContactUid) {
    console.warn("[performSmartContactCleanup] Aborting: No phones and no targetContactUid provided.");
    return;
  }

  const targetDigitsSet = new Set<string>();
  for (const phone of phoneList) {
    const digits = phone.replace(/\D/g, "");
    if (!digits) continue;
    targetDigitsSet.add(digits);
    const searchDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    targetDigitsSet.add(searchDigits);
  }

  console.log("[performSmartContactCleanup] Target digit patterns:", Array.from(targetDigitsSet));

  try {
    const contactsMap = new Map<string, IContact>();

    if (effectiveCompanyId) {
      // 1. Fetch un-queried contacts list to guarantee we inspect all contacts (in case backend search misses phone numbers)
      const listRes = await getContacts(effectiveCompanyId, 1, 100, undefined, "");
      const list = (Array.isArray(listRes) ? listRes : listRes?.data) || [];
      console.log(`[performSmartContactCleanup] Fetched ${list.length} contacts for companyId ${effectiveCompanyId}`);
      if (Array.isArray(list)) {
        for (const c of list) {
          if (c.uid) contactsMap.set(c.uid, c);
        }
      }

      // 2. Also try searching for searchDigits of each phone number
      for (const phone of phoneList) {
        const digits = phone.replace(/\D/g, "");
        if (!digits) continue;
        const searchDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

        const resWithSearch = await getContacts(effectiveCompanyId, 1, 100, searchDigits, "");
        const searchList = (Array.isArray(resWithSearch) ? resWithSearch : resWithSearch?.data) || [];
        if (Array.isArray(searchList)) {
          for (const c of searchList) {
            if (c.uid) contactsMap.set(c.uid, c);
          }
        }
      }
    }

    if (targetContactUid && !contactsMap.has(targetContactUid)) {
      contactsMap.set(targetContactUid, { uid: targetContactUid, phoneNumbers: [] });
    }

    console.log(`[performSmartContactCleanup] Total unique contacts to inspect: ${contactsMap.size}`);

    for (const c of Array.from(contactsMap.values())) {
      if (!c.uid) continue;

      const topLevelDigits = String(c.phoneNumber || "").replace(/\D/g, "");
      const topLevelClean = topLevelDigits.length === 11 && topLevelDigits.startsWith("1") ? topLevelDigits.slice(1) : topLevelDigits;
      const matchesTopLevel = targetDigitsSet.size > 0 && (targetDigitsSet.has(topLevelDigits) || targetDigitsSet.has(topLevelClean));

      const existingPhones = c.phoneNumbers || [];
      const remainingPhones = existingPhones.filter((p: any) => {
        const pDigits = String(p.number || "").replace(/\D/g, "");
        const pClean = pDigits.length === 11 && pDigits.startsWith("1") ? pDigits.slice(1) : pDigits;
        return !targetDigitsSet.has(pDigits) && !targetDigitsSet.has(pClean);
      });

      const phoneWasRemoved = remainingPhones.length < existingPhones.length || matchesTopLevel;
      const isTargetContact = !!targetContactUid && c.uid === targetContactUid;

      console.log(`[performSmartContactCleanup] Inspecting contact ${c.uid} (${c.name}):`, {
        phoneWasRemoved,
        isTargetContact,
        existingCount: existingPhones.length,
        remainingCount: remainingPhones.length,
      });

      if (!phoneWasRemoved && !isTargetContact) {
        continue;
      }

      if (remainingPhones.length > 0) {
        console.log(`[performSmartContactCleanup] Updating contact ${c.uid} with remaining ${remainingPhones.length} phones`);
        if (!remainingPhones.some((p: any) => p.isPrimary)) {
          remainingPhones[0].isPrimary = true;
        }
        await updateContact(c.uid, {
          uid: c.uid,
          companyId: c.companyId || effectiveCompanyId || 0,
          phoneNumbers: remainingPhones.map((p: any) => ({
            number: p.number,
            label: p.label || "other",
            isPrimary: !!p.isPrimary,
          })),
          name: c.name || "",
          lastName: c.lastName || null,
          email: c.email || null,
          notes: c.notes || null,
          type: c.type || "OTHER",
        } as any);
      } else {
        console.log(`[performSmartContactCleanup] DELETING contact ${c.uid} (${c.name})`);
        try {
          await deleteContact(c.uid);
          console.log(`[performSmartContactCleanup] Successfully deleted contact ${c.uid}`);
        } catch (deleteErr) {
          console.error(`[performSmartContactCleanup] Error deleting contact ${c.uid}:`, deleteErr);
        }
      }
    }

    if (queryClient) {
      await invalidateContactResolutionQueries(queryClient, effectiveCompanyId);
    }
  } catch (error) {
    console.error("[performSmartContactCleanup] Failed to perform contact cleanup after linking:", error);
  }
};
